/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { useAppContext } from "@qlik/qlik/src/context/AppContext";
import PricingFeatureTableHeaderCard from "@qlik/qlik/src/components/Card/PricingFeatureTableHeaderCard/PricingFeatureTableHeaderCard";
import { getMicrocopyValueFromKeys, handleKeyboardEventBehavior } from "@qlik/qlik/src/lib/util/helpers";
import { PRICING_TABLE_VALUES } from "@qlik/qlik/src/lib/constants/pricing-constants";
import formatContentfulRichText from "@qlik/qlik/src/lib/format-rich-text-content";
import * as styles from "./PricingFeatureTable.module.css";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const PricingFeatureTable = ({ data }) => {
    const [isMounted, setIsMounted] = useState(false);
    const [showSecondSection, setShowSecondSection] = useState(false);
    const [isFirstSectionVisible, setIsFirstSectionVisible] = useState(false);
    const firstSectionRef = useRef(null);
    const { siteScrolledDown, pricingFeaturedColumns } = useAppContext();

    const featuredColumnClassArray =
        // Translate index of featured column in array to nth-child by adding + 2
        // (1 since nth-child is 1 indexed while arrays are 0 indexed, and 1 more since the
        // first column of the table is a description column not the actual product column)
        pricingFeaturedColumns?.map((el, i) => (el ? `featured${i + 2}` : null)).filter((x) => x) ?? [];

    const numberOfColumns = pricingFeaturedColumns?.length;
    const layoutWidthClass = numberOfColumns > 3 ? "compact" : false;

    const [expandText, collapseText] = getMicrocopyValueFromKeys(
        data?.microcopyGroup,
        [PRICING_TABLE_VALUES.expand, PRICING_TABLE_VALUES.collapse],
        true
    );

    const toggleSecondSection = (event) => {
        event.preventDefault();
        setShowSecondSection(!showSecondSection);
    };

    const handleIntersection = (entries) => {
        const [entry] = entries;
        setIsFirstSectionVisible(entry.isIntersecting);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(handleIntersection, {
            rootMargin: "-5% 0% -95% 0%",
        });

        if (firstSectionRef.current) {
            observer.observe(firstSectionRef.current);
        }

        setIsMounted(true);

        return () => {
            if (firstSectionRef.current) {
                observer.unobserve(firstSectionRef.current);
            }
        };
    }, []);

    return (
        <>
            <section
                ref={firstSectionRef}
                className={`first-section tb-7.5 lg:pb-12.5 ${isFirstSectionVisible ? `${styles.tableSection}` : ""}  ${
                    styles.contentTable
                }`}
                id={data?.customId}
            >
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div
                                className={`${cx(
                                    "pricingTable",
                                    featuredColumnClassArray,
                                    layoutWidthClass
                                )} content-table ${siteScrolledDown ? "scrolledDown" : ""}`}
                            >
                                {data?.cardCollections?.[0].cardItems?.[0].body?.raw && (
                                    <div>
                                        {isMounted &&
                                            renderRichText(
                                                data?.cardItems?.[0].body,
                                                formatContentfulRichText((cardData) => ({
                                                    ContentfulCard: <PricingFeatureTableHeaderCard data={cardData} />,
                                                }))
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {data?.subtitle && (
                <section className="py-7.5 lg:py-12.25">
                    <div className="container">
                        <div className="row">
                            <div className="col-12">
                                <div
                                    className="flex flex-col md:flex-row items-center text-center align-middle justify-center cursor-pointer hover:opacity-80"
                                    role="button"
                                    onClick={toggleSecondSection}
                                    tabIndex={-1}
                                    onKeyDown={(e) => {
                                        handleKeyboardEventBehavior(e, () => {
                                            toggleSecondSection(e);
                                        });
                                    }}
                                >
                                    <span className="toggle-section title-5 ">
                                        {showSecondSection ? collapseText : expandText}
                                    </span>
                                    <span
                                        className={`inline-block w-7.5 h-7.5  ml-0 md:ml-7.5 mt-2.5 md:mt-0 bg-contain bg-no-repeat bg-center ${
                                            showSecondSection ? "bg-minusIconGreen" : "bg-plusIconGreen"
                                        }`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
            <section
                className={`pt-4 pb-15 lg:pt-[33px] lg:pb-30 ${styles.contentTable} ${
                    showSecondSection ? "" : "hidden"
                } `}
            >
                <div className={`container`}>
                    <div className="row">
                        <div className="col-12">
                            <h2 className=" text-center">{data?.title}</h2>
                            {data?.cardCollections[0].cardItems?.map((card) => (
                                <div className=" mt-10 lg:mt-25" key={card.id}>
                                    <div className="flex items-center mb-7.5 lg:mb-17.5">
                                        {card.title && <h3 className=" inline-flex text-gray">{card.title}</h3>}
                                        <div className=" flex-grow ml-7.5 h-0.25 bg-stone" />
                                    </div>
                                    <div
                                        className={`${cx(
                                            "pricingTable",
                                            featuredColumnClassArray,
                                            layoutWidthClass
                                        )} content-table ${siteScrolledDown ? "scrolledDown" : ""}`}
                                    >
                                        {card.body?.raw && (
                                            <div>
                                                {isMounted &&
                                                    renderRichText(
                                                        card?.body,
                                                        formatContentfulRichText((cardData) => ({
                                                            ContentfulCard: (
                                                                <PricingFeatureTableHeaderCard data={cardData} />
                                                            ),
                                                        }))
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PricingFeatureTable;
